// everything related to resizing, viewport, window, document, elements

// imports --------------------------------------------------------------------
import {getPageCoordinates} from '../js_vendor/GetCoordinates.js';
import * as Vars from './0-vars.js';
import * as Once from './2-once.js';
// import * as Page from './3-page.js';
import * as Scroll from './5-scroll.js';

// set user variables ---------------------------------------------------------
const resizeHolders = ['.main', '.popover']; // comment to turn off resize observer

// set code variables ---------------------------------------------------------
export let windowWidth = 0;
export let windowInnerWidth = 0;
export let windowHeight = 0;
export let windowInnerHeight = 0;
export let viewportHeight = 0;
export let fullTouch = 0;
export let navbarHeight = 0;
export let navbarPresent = true;
export let navbarCurrentHeight = 0;

export let documentWidth = 0;
export let documentHeight = 0;
export let maxBodyScrollTop = 0;
export let maxBodyScrollLeft = 0;

export let blockResizeObserver = false; // set to true during animations to not call setResize

export let headerHeight = 0;
export let afmHeight = 0;

export function setBlockResizeObserver(value) { blockResizeObserver = value; }

const rootStyle = document.documentElement.style;

let prevInnerWidth = 0;
let prevObserverWidth = 0;
let prevObserverHeight = 0;

let footerSocialsWidth = 0;

let first = true;
let observerResizeTimer = null;

// resize listeners ------------------------------------------------------------
export const initResize = () => {
  // visual viewport resize listener
  window.visualViewport.addEventListener('resize', setResize);

  Vars.mqPortrait.addEventListener('change', () => {
    setTimeout(setResize, 100);
  });

  // resize observer
  if (typeof resizeHolders !== 'undefined') {
    for (let i = 0; i < resizeHolders.length; i++) {
      // add an observer for each holder that resizes
      const observedEls = document.querySelectorAll(resizeHolders[i]);
      observedEls.forEach((observedEl) => {
        if (observedEl !== null) new ResizeObserver(observerResize).observe(observedEl);
      });
    }
  }

  // run once even if body is fixed
  doSaveOffsets();
};

// resize function -------------------------------------------------------------
export const setResize = (force = false) => {
  if (typeof force === 'object') force = false;
  // console.log('setResize', force);

  // do resizing actions
  const ftEl = document.querySelector('.full-touch');
  if (!ftEl) return;
  fullTouch = ftEl.offsetHeight; // height including navbar on touch
  windowInnerHeight = window.innerHeight; // height minus current navbar on touch
  navbarCurrentHeight = fullTouch - windowInnerHeight; // current navbar on touch
  if (first) navbarHeight = navbarCurrentHeight; // navbar height
  first = false;
  windowHeight = fullTouch - navbarHeight; // height minus navbar on touch
  windowWidth = document.documentElement.clientWidth; // width without scrollbar
  windowInnerWidth = window.innerWidth; // width with scrollbar
  viewportHeight = window.visualViewport.height; // height minus current navbar and keyboard on touch
  navbarPresent = windowInnerHeight === windowHeight ? 1 : 0;

  rootStyle.setProperty('--window-width', windowWidth + 'px');
  rootStyle.setProperty('--window-height', windowHeight + 'px');
  rootStyle.setProperty('--inner-height', windowInnerHeight + 'px'); // sometimes wrong on android
  rootStyle.setProperty('--viewport-height', viewportHeight + 'px');
  rootStyle.setProperty('--navbar-height', navbarHeight + 'px');
  rootStyle.setProperty('--current-navbar-height', navbarCurrentHeight + 'px');

  // on touch, if the body is fixed, menu is always present
  // so adding height: 100% is the way to add a correct size popover
  // or by setting height to windowHeight with js

  if ('checkBlockScroll' in Scroll) Scroll.checkBlockScroll(); // before resizing

  // when resizing width or height ---------------------------------------------
  // resize things

  // save sizes for css, use Rect because of subpixel values!!
  const headerEl = document.querySelector('.header');
  if (headerEl) {
    headerHeight = headerEl.getBoundingClientRect().height;
    rootStyle.setProperty('--header-height', headerHeight + 'px');
  }

  const afmEl = document.querySelector('.afm');
  if (afmEl) {
    afmHeight = afmEl.getBoundingClientRect().height;
    rootStyle.setProperty('--afm-height', afmHeight + 'px');
  }

  const footerSocialsEl = document.querySelector('.footer__socials');
  if (footerSocialsEl) {
    footerSocialsWidth = footerSocialsEl.getBoundingClientRect().width;
    rootStyle.setProperty('--footer-socials-width', footerSocialsWidth + 'px');
  }

  // check if resize width or forced
  if (windowInnerWidth === prevInnerWidth && force === false) {
    saveOffsets();
    Scroll.setScroll();
    return;
  }

  prevInnerWidth = windowInnerWidth;

  // from here is skipped when only resizing height ----------------------------
  // console.log('resizing width');

  // logo repeater speed
  const repeaterEls = document.querySelectorAll('.ticker__repeater');
  repeaterEls.forEach(repeaterEl => {
    const rect = repeaterEl.getBoundingClientRect();
    const {width} = rect;
    // set css animation duration based on width
    const duration = width / 100;
    repeaterEl.style.setProperty('--duration', duration + 's');
  });

  // last
  saveOffsets();
  Scroll.setScroll();
};

// functions -------------------------------------------------------------------
export const saveOffsets = () => {
  // console.log('saveOffsets');

  if ('fixer' in Once && typeof Once.fixer?.bodyFixed !== 'undefined') {
    if (Once.fixer.bodyFixed !== true) {
      doSaveOffsets();
    }
  } else {
    doSaveOffsets();
  }
};

const doSaveOffsets = () => {
  // console.log('doSaveOffsets'); 
  // always call this through saveOffsets

  // save offsets here
  documentHeight = document.documentElement.scrollHeight; // entire document height
  maxBodyScrollTop = documentHeight - windowInnerHeight;
  rootStyle.setProperty('--document-height', documentHeight + 'px');

  documentWidth = document.documentElement.scrollWidth; // entire document width
  maxBodyScrollLeft = documentWidth - windowWidth;

  // documentHeight based calculations
  const headerBtnsEl = document.querySelector('.buttons--header');
  const headerLogoEl = document.querySelector('.header__logo');
  if (headerBtnsEl && headerLogoEl) {
    const rect = headerBtnsEl.getBoundingClientRect();
    headerLogoEl.style.setProperty('--header-btns-width', rect.width + 'px');
  }
};

// check for resizing of holders
const observerResize = () => {
  clearTimeout(observerResizeTimer);

  // in timeout to prevent multiple calls when resizing window
  observerResizeTimer = setTimeout(() => {
    // console.log('observerResize', blockResizeObserver, windowWidth, prevObserverWidth, windowHeight, prevObserverHeight);

    // prevent double resize when resizing window
    if (
      (windowWidth !== prevObserverWidth || windowHeight !== prevObserverHeight)
      && prevObserverWidth !== 0
    ) {
      prevObserverWidth = windowWidth;
      prevObserverHeight = windowHeight;
      return;
    }

    prevObserverWidth = windowWidth;
    prevObserverHeight = windowHeight;

    // not animating? do resize
    if (!blockResizeObserver) setResize(true);
  }, 0);
};

